import React, { useCallback, useEffect, useState } from 'react';
import { Modal, Tappable } from '@vectord/components';
import { batch, useDispatch, useSelector } from 'react-redux';
import cogoToast from 'cogo-toast';

import { DEFAULT_ERROR_MESSAGE } from 'data/defaultErrorMessage';
import {
  addSuperBatchModuleModalStateSelector,
  selectedSuperBatchModuleSelector,
  superBatchModuleFieldErrorSelector,
  setAddSuperBatchModuleModalState,
  setSelectedSuperBatchModule,
  setSuperBatchModuleErrors,
  useCreateSuperBatchModuleMutation,
} from 'features/moduleManagement';
import AddSuperBatchModuleModalField from '../AddSuperBatchModuleModalField';
import FieldErrors from 'components/FieldErrors';
import GlobalLoader from 'components/GlobalLoader';
import styles from './AddSuperBatchModuleModal.module.scss';

function AddSuperBatchModuleModal({ superBatch }) {
  const dispatch = useDispatch();
  const selectedModule = useSelector(selectedSuperBatchModuleSelector);
  const isModalOpen = useSelector(addSuperBatchModuleModalStateSelector);
  const fieldErrors = useSelector(superBatchModuleFieldErrorSelector);
  const [createSuperBatchModule, { error, isSuccess, isLoading }] =
    useCreateSuperBatchModuleMutation();

  const [moduleType, setModuleType] = useState(null);
  const [interviewRequired, setInterviewRequired] = useState(null);
  const [academyModuleType, setAcademyModuleType] = useState(null);
  const [academyModule, setAcademyModule] = useState(null);

  const handleInterviewRequired = useCallback(
    (event) => {
      dispatch(setSuperBatchModuleErrors(null));
      setInterviewRequired(event.target.checked);
    },
    [dispatch]
  );

  const handleOnClose = useCallback(() => {
    batch(() => {
      dispatch(setAddSuperBatchModuleModalState(false));
      dispatch(setSelectedSuperBatchModule(null));
      setAcademyModule(null);
      setAcademyModuleType(null);
      setInterviewRequired(false);
      setModuleType(null);
    });
  }, [dispatch]);

  const handleAcademyModuleChange = useCallback(
    (option) => {
      dispatch(setSuperBatchModuleErrors(null));
      setAcademyModule(option);
    },
    [dispatch]
  );

  const handleConfirm = useCallback(() => {
    if (
      !academyModule?.value ||
      !academyModuleType ||
      !selectedModule?.academy_module_id ||
      !superBatch?.id
    ) {
      dispatch(setSuperBatchModuleErrors('Fill all the fields correctly!'));
      return;
    } else {
      dispatch(setSuperBatchModuleErrors(null));
    }

    createSuperBatchModule({
      academyModuleId: academyModule.value,
      academyModuleType: academyModuleType,
      beforeModuleId: selectedModule.academy_module_id,
      interviewRequired: interviewRequired,
      superBatchId: superBatch.id,
    });
  }, [
    academyModule,
    academyModuleType,
    createSuperBatchModule,
    dispatch,
    interviewRequired,
    selectedModule,
    superBatch,
  ]);

  const handleOnChange = useCallback(
    (option) => {
      dispatch(setSuperBatchModuleErrors(null));
      setAcademyModuleType(option.value);
    },
    [dispatch]
  );

  const handleModuleTypeChange = useCallback(
    (option) => {
      dispatch(setSuperBatchModuleErrors(null));
      setModuleType(option);
      setAcademyModule(null);
    },
    [dispatch]
  );

  useEffect(() => {
    if (isSuccess) {
      cogoToast.success('Module is successfully Added!');
      dispatch(setAddSuperBatchModuleModalState(false));
      dispatch(setSelectedSuperBatchModule(null));
      setAcademyModule(null);
      setAcademyModuleType(null);
      setInterviewRequired(false);
      setModuleType(null);
    }
  }, [dispatch, isSuccess]);

  useEffect(() => {
    if (error) {
      cogoToast.error(error?.data?.message || DEFAULT_ERROR_MESSAGE);
    }
  }, [error]);

  return (
    <Modal
      isOpen={isModalOpen}
      unMountOnClose
      onClose={handleOnClose}
      withoutHeader
      className={styles.root}
      canClose={!isLoading}
    >
      <GlobalLoader isVisible={isLoading} />
      <div className="bold dark">ADD ACADEMY MODULE</div>
      <div className="dark m-t-20">
        A new module will be added below the selected module. You can choose the
        module type (Core or Career) and specify the module to add.
      </div>
      <AddSuperBatchModuleModalField
        superBatch={superBatch}
        selectedModule={selectedModule}
        interviewRequired={interviewRequired}
        handleInterviewRequired={handleInterviewRequired}
        academyModuleType={academyModuleType}
        handleOnChange={handleOnChange}
        moduleType={moduleType}
        handleModuleTypeChange={handleModuleTypeChange}
        academyModule={academyModule}
        handleAcademyModuleChange={handleAcademyModuleChange}
      />
      <FieldErrors
        className="m-t-10"
        name={'superBatchModuleErrors'}
        errors={fieldErrors}
      />
      <div className="m-t-m m-b-10 row">
        <Tappable
          onClick={handleConfirm}
          disabled={isLoading}
          className="btn btn-primary btn-long m-r-xs"
        >
          Add Module
        </Tappable>
      </div>
    </Modal>
  );
}

export default AddSuperBatchModuleModal;
